import { SystemHealthOutageDto } from '../types/system-health-api.types';
import { ReportLine, ReportWindow } from '../types/system-health.types';

/** The backend's own heartbeat-gap rows carry this key; they describe missing data, not an outage. */
const MONITORING_KEY = 'monitoring';

/**
 * Folds outage windows into one report line per dependency: every slot listed, an
 * open window left open-ended. Monitoring gaps — windows where nothing was observed
 * at all — get one line of their own; the API can deliver the same gap both as
 * `monitoringGaps` and as a "monitoring"-keyed item, so both feed one pool and
 * overlapping windows merge into a single slot.
 *
 * The result carries data, not sentences: the clock and the locale belong to the
 * view, which is the only place that can re-render when either changes.
 *
 * @param outages - Outage windows from the history response.
 * @param monitoringGaps - Heartbeat gaps from the history response.
 * @param monitoringGapLabel - Label for the single gap line.
 * @returns One line per dependency, sorted by label.
 */
export function buildReportLines(
  outages: SystemHealthOutageDto[],
  monitoringGaps: SystemHealthOutageDto[] = [],
  monitoringGapLabel = ''
): ReportLine[] {
  const gapPool = [...monitoringGaps, ...outages.filter(outage => outage.key === MONITORING_KEY)];

  const byKey = new Map<string, { label: string; outages: SystemHealthOutageDto[] }>();
  for (const outage of outages) {
    if (outage.key === MONITORING_KEY) continue;
    const entry = byKey.get(outage.key) ?? { label: outage.label || outage.key, outages: [] };
    entry.outages.push(outage);
    byKey.set(outage.key, entry);
  }

  const lines = [...byKey.entries()].map(([key, entry]) => toLine(key, entry.label, entry.outages, false));
  if (gapPool.length > 0) {
    lines.push(toLine(MONITORING_KEY, monitoringGapLabel, gapPool, true));
  }
  return lines.sort((first, second) => first.label.localeCompare(second.label));
}

function toLine(key: string, label: string, outages: SystemHealthOutageDto[], isGap: boolean): ReportLine {
  return { key, label, windows: mergeWindows(outages), isGap };
}

/**
 * Unions overlapping windows: a fleet-wide incident is stored per service (four
 * Debezium rows for one Kafka outage) but reads as ONE slot on the report — the
 * same "just say it was down" rule the live rows follow.
 */
function mergeWindows(outages: SystemHealthOutageDto[]): ReportWindow[] {
  const ordered = [...outages].sort((first, second) => first.startedAt - second.startedAt);
  const merged: { startedAt: number; endedAt: number; services: Set<string> }[] = [];

  for (const outage of ordered) {
    const last = merged[merged.length - 1];
    const lastEnd = last && (last.endedAt || Number.POSITIVE_INFINITY);
    if (last && outage.startedAt <= lastEnd) {
      const outageEnd = outage.endedAt || Number.POSITIVE_INFINITY;
      last.endedAt =
        Math.max(lastEnd, outageEnd) === Number.POSITIVE_INFINITY ? 0 : Math.max(last.endedAt, outage.endedAt);
      for (const service of affectedServices(outage)) last.services.add(service);
    } else {
      merged.push({
        startedAt: outage.startedAt,
        endedAt: outage.endedAt,
        services: new Set(affectedServices(outage)),
      });
    }
  }

  return merged.map(window => ({
    startedAt: window.startedAt,
    endedAt: window.endedAt,
    services: [...window.services].sort(),
  }));
}

function affectedServices(outage: SystemHealthOutageDto): string[] {
  if (outage.service) return [outage.service];
  return (outage.observedBy ?? []).map(observer => observer.service).filter(Boolean);
}
